import { useElementOffsetHeight } from '@/hooks';
import {
  HEADER_ID_FOR_OFFSET_HEIGHT,
  PS_HEADER_ID_FOR_OFFSET_HEIGHT,
  isPS,
} from '@/utils';
import { Table as AntTable, type TableProps } from 'antd';
import type { AnyObject } from 'antd/es/_util/type';
import type {
  SELECTION_ALL,
  SELECTION_INVERT,
  SELECTION_NONE,
} from 'antd/es/table/hooks/useSelection';
import classNames from 'classnames';
import type { Reference, Summary } from 'rc-table';
import {
  type PropsWithChildren,
  type ReactElement,
  type Ref,
  forwardRef,
} from 'react';
import styles from './Table.module.scss';

type CustomTableProps<T extends AnyObject = AnyObject> = Omit<
  TableProps<T>,
  'size'
> & {
  enableSticky?: boolean;
  isRowHighlighted?: (record: T) => boolean;
};

type CustomRefTable = <T extends AnyObject = AnyObject>(
  props: PropsWithChildren<CustomTableProps<T>> & {
    ref?: Ref<Reference>;
  },
) => ReactElement;

const CustomTable = <T extends AnyObject = AnyObject>(
  props: CustomTableProps<T>,
  ref: Ref<Reference>,
) => {
  const { enableSticky = true, isRowHighlighted, onRow, ...rest } = props;

  const headerHeight = useElementOffsetHeight({
    enabled: enableSticky,
    targetId: isPS()
      ? PS_HEADER_ID_FOR_OFFSET_HEIGHT
      : HEADER_ID_FOR_OFFSET_HEIGHT,
  });

  const handleRow = (record: T, index?: number) => {
    const rowProps = onRow ? onRow(record, index) : {};
    return {
      ...rowProps,
      style: {
        ...(rowProps.style || {}),
        cursor: rowProps.onClick ? 'pointer' : undefined,
      },
    };
  };

  return (
    <AntTable
      sticky={
        enableSticky
          ? {
              offsetHeader: headerHeight,
            }
          : undefined
      }
      scroll={enableSticky ? { x: 'max-content' } : undefined}
      rowClassName={(record) =>
        classNames(isRowHighlighted?.(record) && styles.rowHighlighted)
      }
      onRow={handleRow}
      {...rest}
      size="small"
      ref={ref}
    />
  );
};

// ref: https://github.com/ant-design/ant-design/blob/bef9539e228748f19e7a2f6fb763cf1c021bf142/components/table/Table.tsx#L26-L36
const Table = forwardRef(CustomTable) as unknown as CustomRefTable & {
  SELECTION_ALL: typeof SELECTION_ALL;
  SELECTION_INVERT: typeof SELECTION_INVERT;
  SELECTION_NONE: typeof SELECTION_NONE;
  Summary: typeof Summary;
};

Table.SELECTION_ALL = AntTable.SELECTION_ALL;
Table.SELECTION_INVERT = AntTable.SELECTION_INVERT;
Table.SELECTION_NONE = AntTable.SELECTION_NONE;
Table.Summary = AntTable.Summary;

export { Table };
